import React, { useEffect, useState, useRef, useCallback, FC } from 'react';
import * as url from 'url';
import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';
import { notification, PageHeading, Skeleton } from 'src/common';
import { useTranslation } from 'react-i18next';
import { faBalanceScaleRight } from '@fortawesome/pro-light-svg-icons';
import styles from './index.module.scss';

class RamaClient {
  private readonly endpoint: string;

  constructor() {
    if (!import.meta.env.VITE_RAMA_ENDPOINT) {
      throw new Error(
        'No rama endpoint, please enter VITE_RAMA_ENDPOINT=https://staging.rama.tendium.net/ in the env file. '
      );
    }

    this.endpoint = import.meta.env.VITE_RAMA_ENDPOINT;
  }

  public getSrc(): string | null {
    return url.parse(this.endpoint).host;
  }

  public getDomain(): string | null {
    return url.parse(this.endpoint).href;
  }

  public getIframeSrc(): string {
    return url.resolve(this.endpoint, '/listen');
  }

  public async getSessionToken(): Promise<string> {
    // Get cookie
    const cognitoToken = await Auth.currentSession();

    try {
      const res = await fetch(url.resolve(this.endpoint, '/authenticate'), {
        method: 'post',

        body: JSON.stringify({
          cognito_token: cognitoToken.getAccessToken().getJwtToken()
        }),

        headers: {
          'Content-Type': 'application/json'
        }
      });
      const json = await res.json();
      return json.session_token as string;
    } catch (e) {
      throw new Error('not valid token');
    }
  }
}

const client = new RamaClient();
type DashBoardState = 'loading' | 'error';
const DashBoardFrame: FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<DashBoardState | null>('loading');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const onError = useCallback(
    (msg: string) => {
      Sentry.captureMessage(msg);
      notification.error({
        message: t('RAMA.ramaError'),
        description: msg
      });
      setState('error');
    },
    [t, setState]
  );
  console.debug({ state });
  const onMessage = useCallback(
    (event: MessageEvent): void => {
      if (url.parse(event.origin).host === client.getSrc()) {
        if (event.data.ok === true) {
          setState(null);
        } else {
          onError(event.data.message);
        }
      }
    },
    [onError]
  );

  useEffect(() => {
    if (!state) {
      window.addEventListener('message', onMessage);
      return () => {
        window.removeEventListener('message', onMessage);
      };
    }
  }, [onMessage, state]);

  const onLoadIframe = useCallback(async (): Promise<void> => {
    const currentIframe = iframeRef.current;
    if (currentIframe) {
      try {
        const token = await client.getSessionToken();
        currentIframe.contentWindow?.postMessage({ session_token: token }, client.getDomain() as string);
      } catch (e) {
        setState('error');
      }
    }
  }, [iframeRef]);

  return (
    <>
      <div className={styles.dashboard}>
        <PageHeading title={t('Navigation.rama')} icon={faBalanceScaleRight} />
        <iframe
          onError={() => onError('Not able to read in iframe')}
          onLoad={onLoadIframe}
          ref={iframeRef}
          name={'rama'}
          className={styles.dashboardIframe}
          hidden={state !== null}
          title={'Rama'}
          src={client.getIframeSrc()}
        ></iframe>
        {state === 'loading' && <Skeleton active loading />}
        {state === 'error' && (
          <div className={styles.desc}>
            {`Sorry we cannot retrieve this data at this moment please check again later or contact support. 
              (c) Robert`}
          </div>
        )}
      </div>
    </>
  );
};

export default DashBoardFrame;
